import { useEffect, useRef } from 'react';

import { domainLogosSelectors } from 'app-components/flux/domain-logos';
import { fetchLogosByUrl } from 'app-components/flux/domain-logos/domainLogosSlice';
import { useAppDispatch, useAppSelector } from 'app-components/hooks/redux';
import type RootState from 'app-components/utils/redux-store/rootState';

import type { Biller } from './billPresentmentTypes';
import { extractHostname } from './billPresentmentUtils';

const emptyArray: Biller[] = [];

const selectBillersWithLogos = domainLogosSelectors.makeSelectItemsWithLogos<Biller>(extractHostname);

// ## -- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -- ##
// ## --    Multi-biller logo hydration, user for multi-biller displays    -- ##
// ## -- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -- ##
export const useBillersWithLogos = (billers: Biller[] | null) => {
  const dispatch = useAppDispatch();

  const domainLogos = useAppSelector(domainLogosSelectors.stdSliceSelectors.selectEntities);
  const fetchedDomains = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!billers || billers.length === 0) return;

    const domainsToFetch = billers.map((b: Biller) => extractHostname(b));
    const newDomains = domainsToFetch.filter((domain) => {
      if (!domain) return false;
      const alreadyFetched = fetchedDomains.current.has(domain);
      const notInStore = !domainLogos?.[domain];
      return !alreadyFetched && notInStore;
    });

    if (newDomains.length > 0) {
      newDomains.forEach((d) => fetchedDomains.current.add(d));
      dispatch(fetchLogosByUrl(newDomains) as any);
    }
  }, [billers, dispatch, domainLogos]);

  const billersWithLogos = useAppSelector((state: RootState) =>
    billers ? selectBillersWithLogos(state, billers) : emptyArray,
  );

  return billersWithLogos;
};

const billerFetchedDomains = new Set();

// ## -- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  -- ##
// ## --                  Single-biller logo lookup                   -- ##
// ## -- - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  - -- ##
export const useLogoForBiller = (biller: Biller | null | undefined) => {
  const dispatch = useAppDispatch();

  const billerDomain = biller && extractHostname(biller);
  const billerLogo = useAppSelector((state) =>
    billerDomain ? domainLogosSelectors.selectLogoByUrl(state, billerDomain) : null,
  );

  useEffect(() => {
    if (!billerDomain || billerLogo || billerFetchedDomains.has(billerDomain)) return;
    // fetch logo to store if not already fetching or already retrieved
    dispatch(fetchLogosByUrl([billerDomain]));
    billerFetchedDomains.add(billerDomain);
  }, [billerDomain, billerLogo, dispatch]);

  return billerLogo || null;
};
